import cx from 'classnames';
import { useSelector } from 'react-redux';

import Icon from 'components/shared/Icon';
import Clock from 'components/shared/Icon/icons/Clock';
import Files from 'components/shared/Icon/icons/Files';
import Folder from 'components/shared/Icon/icons/Folder';
import Home from 'components/shared/Icon/icons/Home';
import Image from 'components/shared/Icon/icons/Image';
import Puzzle from 'components/shared/Icon/icons/Puzzle';
import RectangleChecked from 'components/shared/Icon/icons/RectangleChecked';
import Users from 'components/shared/Icon/icons/Users';
import {
  CONTACTS_PATH,
  CONTENT_LIBRARY_PATH,
  DOCUMENT_LIBRARY_PATH,
  ITINERARIES_PATH,
  MEDIA_LIBRARY_PATH,
  PENDING_CONTENT_PATH,
  RESOURCE_LIBRARY_PATH,
  TASKS_PATH,
  USERS_PATH,
} from 'constants/routes';
import { useAccountPath } from 'hooks/useAccountPath';
import { canViewUsersDashboardSelector, isAgentSelector, isSuperAdminSelector } from 'store/user/selectors';

import s from './index.module.css';
import ItemLink from './ItemLink';

type Props = {
  linkClassName?: string;
};

function Links(props: Props) {
  const { linkClassName } = props;

  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const isAgent = useSelector(isAgentSelector);
  const getPath = useAccountPath();
  const canViewUsersDashboard = useSelector(canViewUsersDashboardSelector);

  return (
    <div className={s.links} data-e2e-element-type="list">
      {(isSuperAdmin || isAgent || canViewUsersDashboard) && (
        <dl>
          <dt id="sidebarHome" className={s.groupTitle}>
            Home
          </dt>
          <dd aria-labelledby="sidebarHome" className={s.definitionContent}>
            <ul className={s.groupList}>
              {canViewUsersDashboard && (
                <li>
                  <ItemLink
                    href={getPath(USERS_PATH)}
                    exact
                    className={cx(s.linkItem, linkClassName)}
                    linkClassName={cx(s.link, s.users)}
                    noUnderline
                    isSSR
                  >
                    <Icon component={Users} fill="none" width="2.2rem" height="2.2rem" />
                    <span>Users</span>
                  </ItemLink>
                </li>
              )}

              {isSuperAdmin && (
                <li>
                  <ItemLink
                    href={getPath(PENDING_CONTENT_PATH)}
                    className={cx(s.linkItem, linkClassName)}
                    linkClassName={cx(s.link, s.pendingContent)}
                    noUnderline
                  >
                    <Icon component={Clock} fill="none" stroke="currentColor" width="2.2rem" height="2.2rem" />
                    <span>Pending Content</span>
                  </ItemLink>
                </li>
              )}

              {(isSuperAdmin || isAgent) && (
                <li>
                  <ItemLink
                    href={getPath(ITINERARIES_PATH)}
                    className={cx(s.linkItem, linkClassName)}
                    linkClassName={cx(s.link, s.dashboard)}
                    noUnderline
                    isSSR
                  >
                    <Icon component={Home} fill="none" width="2.2rem" height="2.2rem" />
                    <span>Dashboard</span>
                  </ItemLink>
                </li>
              )}
            </ul>
          </dd>
        </dl>
      )}

      {isAgent && (
        <dl>
          <dt id="sidebarOperations" className={s.groupTitle}>
            Operations
          </dt>
          <dd aria-labelledby="sidebarOperations" className={s.definitionContent}>
            <ul className={s.groupList}>
              <li>
                <ItemLink
                  href={getPath(TASKS_PATH)}
                  className={cx(s.linkItem, linkClassName)}
                  linkClassName={cx(s.link, s.tasks)}
                  noUnderline
                  isSSR
                >
                  <Icon component={RectangleChecked} fill="none" width="2.2rem" height="2.2rem" />
                  <span>Tasks</span>
                </ItemLink>
              </li>

              <li>
                <ItemLink
                  href={getPath(CONTACTS_PATH)}
                  className={cx(s.linkItem, linkClassName)}
                  linkClassName={cx(s.link, s.contacts)}
                  noUnderline
                  isSSR
                >
                  <Icon component={Users} fill="none" width="2.2rem" height="2.2rem" />
                  <span>Contacts</span>
                </ItemLink>
              </li>
            </ul>
          </dd>
        </dl>
      )}

      <dl>
        <dt id="sidebarLibraries" className={s.groupTitle}>
          Libraries
        </dt>
        <dd aria-labelledby="sidebarLibraries" className={s.definitionContent}>
          <ul className={s.groupList}>
            <li>
              <ItemLink
                href={getPath(CONTENT_LIBRARY_PATH)}
                className={cx(s.linkItem, linkClassName)}
                linkClassName={cx(s.link, s.contentLibrary)}
                noUnderline
                isSSR
              >
                <Icon component={Puzzle} fill="none" width="2.2rem" height="2.2rem" />
                <span>Content</span>
              </ItemLink>
            </li>

            {(isSuperAdmin || isAgent) && (
              <li>
                <ItemLink
                  href={getPath(RESOURCE_LIBRARY_PATH)}
                  className={cx(s.linkItem, linkClassName)}
                  linkClassName={cx(s.link, s.resources)}
                  noUnderline
                  isSSR
                >
                  <Icon component={Folder} fill="none" width="2.2rem" height="2.2rem" />
                  <span>Resources</span>
                </ItemLink>
              </li>
            )}

            <li>
              <ItemLink
                href={getPath(DOCUMENT_LIBRARY_PATH)}
                className={cx(s.linkItem, linkClassName)}
                linkClassName={cx(s.link, s.documents)}
                noUnderline
                isSSR
              >
                <Icon component={Files} fill="none" width="2.2rem" height="2.2rem" />
                <span>Documents</span>
              </ItemLink>
            </li>

            <li>
              <ItemLink
                href={getPath(MEDIA_LIBRARY_PATH)}
                className={cx(s.linkItem, linkClassName)}
                linkClassName={cx(s.link, s.media)}
                noUnderline
              >
                <Icon component={Image} width="2.2rem" height="2.2rem" />
                <span>Media</span>
              </ItemLink>
            </li>
          </ul>
        </dd>
      </dl>
    </div>
  );
}

export default Links;
